'use client';

import { BlockHelper } from '@unifyapps/carbon/helpers/BlockHelper';
import { NEW_BLOCK_ID } from '@unifyapps/defs/constants';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import { PageFooter } from '../../../../components/PageLayout/PageFooter';
import { PageHeader } from '../../../../components/PageLayout/PageHeader';
import PageLayoutShell from '../../../../components/PageLayout/PageLayoutShell';
import { PageWorkspace } from '../../../../components/PageLayout/PageWorkspace';
import { useDeviceCapabilities } from '../../../context/DeviceCapabilitiesContext';
import { useRegistryContext } from '../../../context/RegistryProvider';
import {
  useGetDeviceDetailsInStateStore,
  useInterfaceApplicationType,
} from '../../../stores/app/GlobalAppStateStore';
import { useGlobalStateStore } from '../../../stores/GlobalStateStore';
import {
  InterfaceModes,
  useBlockFromPage,
  useInterfaceStore,
} from '../../../stores/InterfaceStore';
import { DocumentTitle } from './DocumentTitle';
import { getPageLayout } from './getPageBody';
import type { InterfacePageProps } from './types';

type Content = { blockIds?: string[] } | undefined;

const getIsBlockEmpty = (content: Content, mode: InterfaceModes) => {
  if (mode === InterfaceModes.BUILDER) return false;
  if (!content) return true;
  if (!content.blockIds?.length) return true;
  return content.blockIds.length === 1 && content.blockIds[0] === NEW_BLOCK_ID;
};

export function InterfacePage({
  name,
  layout,
  className,
  workspaceClassName,
  device,
  BlockRenderer,
  isHeaderEmpty,
  isFooterEmpty,
  colorScheme,
  shouldSetDocumentTitle = true,
  pageScopeKey,
  pageId,
  isNestedInTemplateComponentCanvas = false,
  documentTitle,
  appName,
  mode,
  overrideBodyBlockId,
  showBreadCrumbs = false,
  headerClassName,
}: InterfacePageProps) {
  const { header, body, footer } = getPageLayout(layout);
  const { shouldAnimate } = useDeviceCapabilities();
  const applicationType = useInterfaceApplicationType();
  const isMobileApplication = applicationType === ApplicationType.MOBILE;

  const bodyBlock = useBlockFromPage({
    blockId: layout?.body ?? '',
  });

  /*** * For the TemplateComponent we don't render the body in Canvas rather we render the direct children of body as root elements
   * when called for layout we must render the body but when called for Canvas inside TemplateComponent we don't ***/
  const bodyChildBlockIds = useMemo(() => {
    if (!bodyBlock) return [];
    return BlockHelper.getComponentBlockIds(bodyBlock) || [];
  }, [bodyBlock]);

  return (
    <>
      <DocumentTitle
        appName={appName}
        documentTitle={documentTitle}
        mode={mode}
        name={name}
        shouldSetDocumentTitle={shouldSetDocumentTitle}
      />
      <PageLayoutShell
        className={className}
        dataColorScheme={colorScheme}
        pageId={pageId}
        pageScopeKey={pageScopeKey}
      >
        {isHeaderEmpty ? null : (
          <PageHeader
            headerClassName={clsx('!p-0 !basis-auto', headerClassName)}
            showBreadcrumbs={showBreadCrumbs}
          >
            {header ? <BlockRenderer blockId={header} key={header} parentBlockId={null} /> : null}
          </PageHeader>
        )}
        {body ? (
          <PageWorkspace
            className={clsx(
              'overflow-y-auto',
              {
                'rounded-t-none': !header,
                // todo: remove and verify from web also. @anandabhi04
                // this should not be required.
                'rounded-b-3xl': Boolean(footer) && !isMobileApplication,
                '!me-0': (!header && !footer) || device === DeviceVariantType.MOBILE,
              },
              workspaceClassName,
            )}
            data-animations-enabled={String(shouldAnimate)}
            isMobile={isMobileApplication}
          >
            {isNestedInTemplateComponentCanvas && !overrideBodyBlockId ? (
              bodyChildBlockIds.map((childId) => (
                <BlockRenderer blockId={childId} key={childId} parentBlockId={body} />
              ))
            ) : (
              <BlockRenderer
                blockId={overrideBodyBlockId ?? body}
                key={overrideBodyBlockId ?? body}
                parentBlockId={null}
              />
            )}
          </PageWorkspace>
        ) : null}
        {isFooterEmpty ? null : (
          // we don't want to add padding to the footer in no-code app
          <PageFooter className="!p-0">
            {footer ? <BlockRenderer blockId={footer} key={footer} parentBlockId={null} /> : null}
          </PageFooter>
        )}
      </PageLayoutShell>
    </>
  );
}

export type Props = {
  className?: string;
  workspaceClassName?: string;
  headerClassName?: string;
  colorScheme?: string;
  shouldSetDocumentTitle?: InterfacePageProps['shouldSetDocumentTitle'];
  isNestedInTemplateComponentCanvas?: boolean;
  overrideBodyBlockId?: InterfacePageProps['overrideBodyBlockId'];
};

function InterfacePageContainer(props: Props) {
  const layout = useInterfaceStore().hooks.page.useLayout();
  const name = useInterfaceStore().hooks.page.useName();
  const { device } = useGetDeviceDetailsInStateStore();
  const blocks = useGlobalStateStore().hooks.useBlocks();

  const BlockRenderer = useRegistryContext().registry.getBlockRenderer();
  const { header, footer } = getPageLayout(layout);
  const mode = useInterfaceStore().hooks.useMode();
  const pageScopeKey = useInterfaceStore().hooks.usePageScopeKey();
  const pageId = useInterfaceStore().hooks.useActiveInterfacePageId();

  const isFooterEmpty = footer ? getIsBlockEmpty(blocks[footer]?.content as Content, mode) : true;
  const isHeaderEmpty = header ? getIsBlockEmpty(blocks[header]?.content as Content, mode) : true;
  const documentTitle = useInterfaceStore().hooks.page.useDocumentTitle();
  const appName = useInterfaceStore().hooks.record.useName();

  return (
    <InterfacePage
      appName={appName}
      BlockRenderer={BlockRenderer}
      device={device}
      documentTitle={documentTitle}
      isFooterEmpty={isFooterEmpty}
      isHeaderEmpty={isHeaderEmpty}
      layout={layout}
      mode={mode}
      name={name}
      pageId={pageId}
      pageScopeKey={pageScopeKey}
      {...props}
    />
  );
}

export default InterfacePageContainer;
